import { createSlice } from "@reduxjs/toolkit";
import ProductPageData, { NewVariation, Picture } from "./interface";
import { ProductExtended } from "@interfaces/product";
import { SliceAction } from "store/store";
import { ProductProperty } from "@interfaces/product-characteristics";


const initialState: ProductPageData = {
  authenticProductObject: undefined,
  basicInfo: {
    data: null,
    isValid: false,
  },
  selected_variation: {
    childIds: [],
    parentId: -1,
  },
  pictures: [],
  picturesIsValid: true,
  picturesModal: [],
  properties: {},

  properties_group_id: -1,
  combinationsSelectedVariationValues: [[]],
  listsVariationValues: [],

  activatedChars: false,
  activatedVariations: false,

  errorsChars: {},
  errorsVariations: {}
};

const productPageSlice = createSlice({
  initialState,
  name: "productPage",
  reducers: {
    setAuthenticProductObject: (state, action: SliceAction<ProductPageData["authenticProductObject"]>) => {
      state.authenticProductObject = action.payload;
    },

    setIsValidBasicInfo: (state, action: SliceAction<boolean>) => {
      state.basicInfo.isValid = action.payload;
    },
    setDataBasicInfo: (
      state,
      action: SliceAction<ProductPageData["basicInfo"]["data"]>,
    ) => {
      state.basicInfo.data = action.payload;
    },
    clearDataBasicInfo: (state) => {
      state.basicInfo.data = null;
    },

    addPictures: (state, action: SliceAction<ProductPageData["pictures"]>) => {
      state.pictures = [...state.pictures, ...action.payload];
    },
    setPicures: (state, action: SliceAction<ProductPageData["pictures"]>) => {
      state.pictures = action.payload;
    },
    removePictures: (state, action: SliceAction<Picture["id"]>) => {
      state.pictures = state.pictures.filter(
        (item) => item.id !== action.payload,
      );
    },
    setPicturesIsValid: (state, action: SliceAction<ProductPageData["picturesIsValid"]>) => {
      state.picturesIsValid = action.payload;
    },

    product_selected_variation_addData: (
      state,
      action: SliceAction<{
        childIds: { id: ProductExtended["id"], tag: "single" | "multiple" }[];
        parentId: ProductExtended["id"];
      }>,
    ) => {
      state.selected_variation = {
        parentId: action.payload.parentId,
        childIds: [...state.selected_variation.childIds, ...action.payload.childIds]
      };
    },
    product_selected_variation_deleteData: (state, action: SliceAction<{ id: ProductExtended["id"]; singleOnly: boolean }>) => {
      state.selected_variation.childIds = state.selected_variation.childIds.filter((item) => {
        if (action.payload.singleOnly) {
          return item.id !== action.payload.id || item.tag !== "single";
        } else {
          return item.id !== action.payload.id;
        }
      });
    },
    product_selected_variation_setData: (
      state,
      action: SliceAction<{
        childIds: { id: ProductExtended["id"], tag: "single" | "multiple" }[];
        parentId: ProductExtended["id"];
      }>,
    ) => {
      state.selected_variation = action.payload;
    },
    product_selected_variation_clearData: (state) => {
      state.selected_variation = {
        childIds: [],
        parentId: -1,
      }
    },

    addPicturesModal: (state, action: SliceAction<ProductPageData["picturesModal"]>) => {
      state.picturesModal = [...state.picturesModal, ...action.payload];
    },
    setPicuressModal: (state, action: SliceAction<ProductPageData["picturesModal"]>) => {
      state.picturesModal = action.payload;
    },
    removePicturesModal: (state, action: SliceAction<Picture["id"]>) => {
      state.picturesModal = state.picturesModal.filter(
        (item) => item.id !== action.payload,
      );
    },

    setCombinationsSelected: (state, action: SliceAction<NewVariation[]>) => {
      state.combinationsSelectedVariationValues = generateCombinations(action.payload);
    },
    setProperties: (
      state,
      action: SliceAction<ProductPageData["properties"]>,
    ) => {
      state.properties = action.payload;
    },
    setPropertiesGroupId: (
      state,
      action: SliceAction<ProductPageData["properties_group_id"]>,
    ) => {
      state.properties_group_id = action.payload;
    },

    setVariations: (state, action: SliceAction<ProductPageData["listsVariationValues"]>) => {
      state.listsVariationValues = action.payload;
    },

    setActivatedChars: (state, action: SliceAction<boolean>) => {
      state.activatedChars = action.payload;
    },
    setActivatedVariations: (state, action: SliceAction<boolean>) => {
      state.activatedVariations = action.payload;
    },

    setErrorChars: (state, action: SliceAction<ProductProperty["id"][]>) => {
      action.payload.forEach(id => {
        state.errorsChars[id] = false;
      })
    },

    setErrorVariations: (state, action: SliceAction<ProductProperty["id"][]>) => {
      action.payload.forEach(id => {
        state.errorsVariations[id] = false;
      })
    },

    addErrorChars: (state, action: SliceAction<ProductProperty["id"]>) => {
      state.errorsChars[action.payload] = true;
    },

    addErrorVariations: (state, action: SliceAction<ProductProperty["id"]>) => {
      state.errorsVariations[action.payload] = true;
    },

    removeErrorChars: (state, action: SliceAction<ProductProperty["id"]>) => {
      delete state.errorsChars[action.payload];
    },

    removeErrorVariations: (state, action: SliceAction<ProductProperty["id"]>) => {
      delete state.errorsVariations[action.payload];
    },

    visibleAllErrorProperty: (state) => {
      for (const key in state.errorsChars) {
        state.errorsChars[key] = true;
      }

      for (const key in state.errorsVariations) {
        state.errorsChars[key] = true;
      }
    },

    clearAllProductData: (state) => {
      state.authenticProductObject = undefined;
      state.basicInfo = {
        data: null,
        isValid: false,
      };
      state.pictures = [];
      state.picturesIsValid = true;
      state.picturesModal = [];
      state.combinationsSelectedVariationValues = [[]];
      state.properties = {};
      state.listsVariationValues = [];
      state.selected_variation = {
        childIds: [],
        parentId: -1,
      }

      state.activatedChars = false;
      state.activatedVariations = false;

      state.errorsChars = {};
      state.errorsVariations = {};
    },
  },
});

const { reducer, actions } = productPageSlice;

export default reducer;

export const {
  setAuthenticProductObject,

  setIsValidBasicInfo,
  setDataBasicInfo,
  clearDataBasicInfo,

  addPictures,
  setPicures,
  removePictures,
  setPicturesIsValid,

  addPicturesModal,
  setPicuressModal,
  removePicturesModal,

  setCombinationsSelected,
  setProperties,
  setPropertiesGroupId,

  setActivatedChars,
  setActivatedVariations,

  setVariations,
  
  product_selected_variation_setData,
  product_selected_variation_addData,
  product_selected_variation_deleteData,
  product_selected_variation_clearData,

  setErrorChars,
  setErrorVariations,
  addErrorChars,
  addErrorVariations,
  removeErrorChars,
  removeErrorVariations,
  visibleAllErrorProperty,

  clearAllProductData,
} = actions;

export function generateCombinations(data: NewVariation[]) {
  function combine(
    currentCombination: ProductExtended["variations"],
    remaining: NewVariation[],
  ): ProductExtended["variations"][] {
    if (remaining.length === 0) {
      return [currentCombination];
    }

    const next = remaining[0];
    const rest = remaining.slice(1);
    const combinations: ProductExtended["variations"][] = [];

    next.values.forEach((value) => {
      const newCombination = currentCombination.concat({
        name: next.name,
        value: value,
      });

      combinations.push(...combine(newCombination, rest));
    });

    return combinations;
  }

  return combine([], data);
}